import { toast } from 'react-toastify';
import { createElement } from 'react';
import BackToGameToastWrapper from '../components/BackToGameToast/BackToGameToastWrapper';
import { useAppStore } from 'store/app/appStore';
import { LAST_PLAYED_STORAGE_KEY } from '../../../constants';
import type IGameSettings from '../../../types/models/IGameSettings';

export interface LastPlayedGameData {
    gameKey: string;
    gameName: string;
    gameThumbnail: string;
    gameData: IGameSettings;
}

class BackToGameToastService {
    private readonly TOAST_ID = 'back-to-game';
    private playerId: string | null = null;

    /**
     * Set the player ID for user-specific storage
     */
    setPlayerId(playerId: string): void {
        this.playerId = playerId;
    }

    /**
     * Get the storage key for the current player
     */
    private getStorageKey(): string {
        // Use app-specific key if available, otherwise fallback to common key
        const baseKey = LAST_PLAYED_STORAGE_KEY;
        
        if (this.playerId) {
            return `${baseKey}_${this.playerId}`;
        }
        return baseKey;
    }

    /**
     * Shows the back to game toast with the most recently played game
     */
    async showLastPlayed(game?: LastPlayedGameData | null): Promise<void> {
        try {
            // Prevent duplicate toasts
            if (this.isActive()) {
                return;
            }

            const lastPlayedGame = game !== undefined ? game : this.getLastPlayedGame();

            if (!lastPlayedGame) {
                return;
            }

            // Verify the game exists in current lobby sections
            const { sections } = useAppStore.getState();
            const gameExists = sections.some(section =>
                section.games.some(game => game.gameKey === lastPlayedGame.gameKey)
            );

            if (!gameExists) {
                console.log('[BackToGameToast] Game not found in current lobby sections:', lastPlayedGame.gameKey);
                return;
            }

            // Create and show toast
            this.createToast(lastPlayedGame);

        } catch (error) {
            // Only log error if it's not a timeout (to reduce noise in QA)
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (!errorMessage.includes('timeout')) {
                console.error('[BackToGameToast] Error showing last played game:', error);
            }
        }
    }

    /**
     * Checks if the back to game toast is currently active
     */
    isActive(): boolean {
        return toast.isActive(this.TOAST_ID);
    }

    /**
     * Dismisses the back to game toast
     */
    dismiss(): void {
        toast.dismiss(this.TOAST_ID);
    }

    /**
     * Gets last played game from localStorage
     */
    getLastPlayedGame(): LastPlayedGameData | null {
        try {
            // Get last played games from localStorage using the same key logic as lastPlayedService
            const stored = localStorage.getItem(this.getStorageKey());
            if (!stored) {
                return null;
            }

            const gameIds = JSON.parse(stored);

            if (!Array.isArray(gameIds) || gameIds.length === 0) {
                return null;
            }

            const mostRecentGameId = gameIds[0]; // First item is most recent
            return this.buildGameData(mostRecentGameId);
        } catch (error) {
            console.error('[BackToGameToast] Error reading last played games from localStorage:', error);
            return null;
        }
    }


    /**
     * Builds game data object from game ID
     */
    private buildGameData(gameKey: string): LastPlayedGameData | null {
        if (!gameKey) {
            return null;
        }

        const { sections } = useAppStore.getState();
        
        // Find the game in sections to get its data
        let gameData: IGameSettings | null = null;
        for (const section of sections) {
            const game = section.games.find(g => g.gameKey === gameKey);
            if (game) {
                gameData = game;
                break;
            }
        }

        if (!gameData) {
            return null;
        }

        return {
            gameKey: gameData.gameKey,
            gameName: gameData.name || gameData.gameKey, // Fallback to gameKey if name not available
            gameThumbnail: `/verticals/${gameData.gameKey}`, // Thumbnail path like original
            gameData: gameData // Pass the full game object for favorites
        };
    }

    /**
     * Creates and displays the toast
     */
    private createToast(gameData: LastPlayedGameData): void {
        const toastElement = createElement(BackToGameToastWrapper, {
            gameId: gameData.gameKey,
            gameName: gameData.gameName,
            gameThumbnail: gameData.gameThumbnail,
            gameData: gameData.gameData
        });

        toast(toastElement, {
            toastId: this.TOAST_ID,
            position: 'bottom-right',
            autoClose: false,
            closeOnClick: false,
            draggable: false,
            hideProgressBar: true,
            closeButton: false,
            className: 'back-to-game-toast-wrapper'
        });
    }

}

export const backToGameToastService = new BackToGameToastService();
